import {
  Library,
  Edit3,
  Wand2,
  Grid3X3,
  Heart,
  Clock,
  Layers,
  LayoutGrid,
  Eye,
  Image as ImageIcon,
  Sparkles,
  Sticker,
} from 'lucide-react';
import styles from './Sidebar.module.css';
import type { SidebarProps } from './Sidebar.types';
import type { Mode, SubCategory, ModeConfig } from '@/types';

const modeConfigs: ModeConfig[] = [
  {
    id: 'library',
    label: 'Library',
    icon: 'Library',
    subCategories: [
      { id: 'browse', label: 'Browse', icon: 'Grid3X3' },
      { id: 'favorites', label: 'Favorites', icon: 'Heart' },
      { id: 'recent', label: 'Recent', icon: 'Clock' },
    ],
  },
  {
    id: 'manual',
    label: 'Manual',
    icon: 'Edit3',
    subCategories: [
      { id: 'canvas', label: 'Canvas', icon: 'Grid3X3' },
      { id: 'layers', label: 'Layers', icon: 'Layers' },
    ],
  },
  {
    id: 'ai',
    label: 'AI',
    icon: 'Wand2',
    subCategories: [
      { id: 'template', label: 'Design', icon: 'LayoutGrid' },
      { id: 'preview', label: 'Preview', icon: 'Eye' },
    ],
  },
  {
    id: 'canvas',
    label: 'Canvas',
    icon: 'Sparkles',
    subCategories: [
      { id: 'background', label: 'Background', icon: 'ImageIcon' },
      { id: 'stickers', label: 'Stickers', icon: 'Sticker' },
    ],
  },
];

const iconMap = {
  Library,
  Edit3,
  Wand2,
  Grid3X3,
  Heart,
  Clock,
  Layers,
  LayoutGrid,
  Eye,
  ImageIcon,
  Sparkles,
  Sticker,
};

function getIcon(iconName: string) {
  return iconMap[iconName as keyof typeof iconMap] || Library;
}

export function Sidebar({
  activeMode,
  activeSubCategory,
  onModeChange,
  onSubCategoryChange,
}: SidebarProps) {
  const activeModeConfig = modeConfigs.find((m) => m.id === activeMode);

  const handleModeClick = (mode: Mode) => {
    onModeChange(mode);
    // Auto-select first sub-category when switching modes
    const config = modeConfigs.find((m) => m.id === mode);
    const firstSubCategory = config?.subCategories[0];
    if (firstSubCategory) {
      onSubCategoryChange(firstSubCategory.id);
    }
  };

  return (
    <aside className={styles.sidebar} aria-label="Design tools">
      {/* Primary Navigation - Mode Selection */}
      <nav className={styles.primaryNav} aria-label="Design modes">
        <ul className={styles.modeList} role="tablist" aria-orientation="vertical">
          {modeConfigs.map(({ id, label, icon }) => {
            const Icon = getIcon(icon);
            const isActive = activeMode === id;
            return (
              <li key={id} role="presentation">
                <button
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`${id}-panel`}
                  className={`${styles.modeButton} ${isActive ? styles.active : ''}`}
                  onClick={() => handleModeClick(id)}
                >
                  <Icon size={20} aria-hidden="true" />
                  <span className={styles.modeLabel}>{label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Visual Divider between panels */}
      <div className={styles.divider} aria-hidden="true" />

      {/* Secondary Navigation - Sub-category Selection */}
      {activeModeConfig && (
        <nav
          className={styles.secondaryNav}
          aria-label={`${activeModeConfig.label} options`}
          id={`${activeMode}-panel`}
          role="tabpanel"
        >
          <ul className={styles.subList}>
            {activeModeConfig.subCategories.map(({ id, label, icon }) => {
              const Icon = getIcon(icon);
              const isActive = activeSubCategory === id;
              return (
                <li key={id}>
                  <button
                    className={`${styles.subButton} ${isActive ? styles.subActive : ''}`}
                    onClick={() => onSubCategoryChange(id as SubCategory)}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <Icon size={18} aria-hidden="true" />
                    <span className={styles.subLabel}>{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </aside>
  );
}
